import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { copyFor } from 'config';
import { trackEvent } from 'domain/tracking';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Modal, Notification } from '@perch-labs/shrike';
import { capitalize, formatDate } from '@perch-labs/warbler';
import { RESEND_DISCLOSURE_EMAIL } from './disclosure-modal.api';
import {
  StyledCancelButton,
  StyledRequestButton,
  StyledText,
} from './disclosure-modal.style';

export interface PersonalizedContract {
  id: number | null;
  utilityAccountId: number | null;
  contractVersionId: number | null;
  contractAgreementId: number | null;
}

export interface UtilityAccountPersonalizedContract {
  utilityAccountId: number;
  personalizedContracts: (PersonalizedContract | null)[] | null;
}
export interface UserDocuSignEnvelope {
  displayName: string | null;
  contractType: string | null;
  status: string | null;
  completedFile: string | null;
  envelopeUpdatedAt: string | null;
  userId: string | null;
  personalizedContractId: string | null;
}
export interface DisclosureProps {
  close: () => void;
  refetch?: () => void;
  isFirstRequest?: boolean;
  user?: {
    id: number;
    originationSource: {
      funnel: string | null;
    } | null;
    userDocuSignEnvelopes?: (UserDocuSignEnvelope | null)[] | null;
  };
  utilityAccountPersonalizedContracts?: UtilityAccountPersonalizedContract[];
  utilityDropDown?: {
    text: string;
    value: number;
    utilityId: number;
    state: string;
  }[];
  disclosureStatus?: string | null;
  contractType?: string | null;
}

const getCopy = copyFor('settings.checkLmiEligibility.disclosureModal');

export const DisclosureModal = ({
  close,
  user,
  utilityAccountPersonalizedContracts,
  refetch,
  isFirstRequest,
  utilityDropDown,
  disclosureStatus,
  contractType,
}: DisclosureProps): JSX.Element => {
  const [isRequestDisabled, setIsRequestDisabled] = useState(false);
  const userDocuSignEnvelopes = useMemo(
    () => (user ? user.userDocuSignEnvelopes : []),
    [user]
  );
  const [lastSentDate, setLastSentDate] = useState('');

  const [selectedUtilityAccount] = useState(
    utilityDropDown?.[0]?.value ?? null
  );
  const [selectedUtility] = useState<number | null>(
    utilityDropDown?.[0]?.utilityId ?? null
  );
  const [selectedState] = useState<string | null>(
    utilityDropDown?.[0]?.state ?? null
  );
  const [disableRequest, setDisableRequest] = useState(false);
  const ldClient = useLDClient();
  const custom = {
    funnel: user?.originationSource?.funnel ?? 'unknown',
    utilityId: selectedUtility!,
  };

  const formName = contractType?.replace('_', ' ');

  useEffect(() => {
    const updateLastSentDate = () => {
      const sortedEnvelopes = userDocuSignEnvelopes
        ?.filter(envelope => envelope?.status === disclosureStatus)
        .sort(
          (a, b) =>
            (new Date(b?.envelopeUpdatedAt ?? 0)?.getTime() ?? 0) -
            (new Date(a?.envelopeUpdatedAt ?? 0)?.getTime() ?? 0)
        );
      if (
        disclosureStatus === 'sent' &&
        sortedEnvelopes &&
        sortedEnvelopes?.length > 0
      ) {
        const docuSignUpdatedAt = sortedEnvelopes.map(envelope => ({
          envelopeUpdatedAt: envelope?.envelopeUpdatedAt,
          personalizedContractId: envelope?.personalizedContractId,
        }));
        const personalizedContracts = utilityAccountPersonalizedContracts?.find(
          account => account?.utilityAccountId === selectedUtilityAccount
        )?.personalizedContracts;
        const disclosureUpdatedAt = personalizedContracts?.map(pc => {
          const envelopeUpdatedAt = docuSignUpdatedAt?.find(
            envelope =>
              Number(envelope?.personalizedContractId) === Number(pc?.id)
          )?.envelopeUpdatedAt;
          return envelopeUpdatedAt;
        });
        if (disclosureUpdatedAt) {
          setLastSentDate(disclosureUpdatedAt[0] ?? '');
          setIsRequestDisabled(true);
        }
      }
    };
    if (userDocuSignEnvelopes && userDocuSignEnvelopes.length > 0) {
      updateLastSentDate();
    }
  }, [
    userDocuSignEnvelopes,
    disclosureStatus,
    utilityAccountPersonalizedContracts,
    selectedUtilityAccount,
  ]);

  useEffect(() => {
    ldClient?.identify({
      key: 'user_key',
      custom,
    });
  }, [custom]);
  const flag = useFlags();
  useEffect(() => {
    setDisableRequest(!flag.disclosureEnabledStates?.includes(selectedState));
  }, [selectedState, flag]);

  let description = '';
  if (isFirstRequest) {
    description = getCopy('firstRequestDescription', {
      formName,
    });
  } else {
    description = lastSentDate ? '' : getCopy('description', { formName });
  }

  const { addNotification } = useContext(Notification.Context);
  const handleNotification = (data: any) => {
    const result = data?.resendDocuSignEmail?.success;
    if (result) {
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('toastNotificationSuccess', { formName }),
        variant: 'success',
      });
      trackEvent('track_user_requested_disclosure', {
        metadata: { selectedUtilityAccount },
      });
    } else {
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message:
          data?.resendDocuSignEmail?.message ||
          getCopy('toastNotificationError'),
        variant: 'error',
      });
    }
  };

  const [resendDisclosureEmail, { loading, error }] = useMutation(
    RESEND_DISCLOSURE_EMAIL
  );

  if (error) {
    handleNotification(false);
    close();
  }

  const handleResendDisclosureEmail = async () => {
    const { data } = await resendDisclosureEmail({
      variables: {
        input: {
          contractType,
          utilityAccountId: selectedUtilityAccount,
          userId: user?.id,
        },
      },
    });
    handleNotification(data);
    refetch?.();
    close();
  };

  return (
    <Modal aria-label="Disclosure Modal" size="medium">
      <Modal.Header title={getCopy('title', { formName })} onClose={close} />
      {disclosureStatus === 'sent' ? (
        <StyledText color="warning" data-testid="last-sent-envelope">
          {getCopy('lastEnvelope', {
            formName: capitalize(formName ?? ''),
            lastSentDate: formatDate(lastSentDate),
          })}
        </StyledText>
      ) : null}
      {disableRequest && !isRequestDisabled ? (
        <StyledText color="error">
          {getCopy('notRequiredForUtility', {
            formName: capitalize(formName ?? ''),
          })}
        </StyledText>
      ) : (
        <StyledText>{description}</StyledText>
      )}
      <StyledRequestButton
        disabled={!!(loading || error) || disableRequest || isRequestDisabled}
        onClick={handleResendDisclosureEmail}
      >
        {getCopy('sendButton', { formName })}
      </StyledRequestButton>
      <StyledCancelButton onClick={close}>
        {getCopy('cancelButton')}
      </StyledCancelButton>
    </Modal>
  );
};
